import { useState, useEffect } from "react";

export interface ScenarioEvent {
  id: string;
  timestamp: string;
  type: "alert" | "info" | "warning" | "critical";
  message: string;
  domain: string;
}

export interface ScenarioAction {
  id: string;
  label: string;
  description: string;
  cost: number;
  effectiveness: number;
  consequences: string;
}

interface ScenarioSimulatorProps {
  title: string;
  description: string;
  events: ScenarioEvent[];
  actions: ScenarioAction[];
  onComplete?: (score: number, passed: boolean) => void;
  onClose?: () => void;
}

export function ScenarioSimulator({ title, description, events, actions, onComplete, onClose }: ScenarioSimulatorProps) {
  const [visibleEvents, setVisibleEvents] = useState<ScenarioEvent[]>([]);
  const [eventIdx, setEventIdx] = useState(0);
  const [takenActions, setTakenActions] = useState<string[]>([]);
  const [remainingBudget, setRemainingBudget] = useState(100);
  const [threatLevel, setThreatLevel] = useState(20);
  const [running, setRunning] = useState(true);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!running || eventIdx >= events.length) return;
    const timer = setTimeout(() => {
      setVisibleEvents((prev) => [...prev, events[eventIdx]]);
      setEventIdx((prev) => prev + 1);
      setThreatLevel((prev) => Math.min(100, prev + 8));
    }, 2000);
    return () => clearTimeout(timer);
  }, [eventIdx, events, running]);

  function handleAction(action: ScenarioAction) {
    if (takenActions.includes(action.id)) return;
    if (remainingBudget < action.cost) return;

    setTakenActions((prev) => [...prev, action.id]);
    setRemainingBudget((prev) => prev - action.cost);
    setThreatLevel((prev) => Math.max(0, prev - action.effectiveness));

    setVisibleEvents((prev) => [
      ...prev,
      {
        id: `action-${action.id}-${Date.now()}`,
        timestamp: new Date().toISOString().slice(11, 19),
        type: "info",
        message: `Action deployed: ${action.label} — ${action.consequences}`,
        domain: "Response",
      },
    ]);

    if (threatLevel - action.effectiveness <= 0) {
      setRunning(false);
      setFinished(true);
      const score = Math.max(0, Math.round(100 - threatLevel + remainingBudget / 2));
      onComplete?.(score, score >= 70);
    }
  }

  useEffect(() => {
    if (eventIdx >= events.length && !finished) {
      setRunning(false);
      setFinished(true);
      const score = Math.max(0, Math.round(100 - threatLevel + remainingBudget / 2));
      onComplete?.(score, score >= 70);
    }
  }, [eventIdx, events.length, finished, threatLevel, remainingBudget, onComplete]);

  const threatColor = threatLevel > 70 ? "var(--error-500)" : threatLevel > 40 ? "var(--warning-500)" : "var(--accent-500)";

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
        <h3 style={{ fontSize: "1.125rem", marginBottom: "0.25rem" }}>{title}</h3>
        <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>{description}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          <div>
            <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Threat Level</div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <div style={{ flex: 1, height: "8px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                <div style={{ width: `${threatLevel}%`, height: "100%", background: threatColor, borderRadius: "var(--radius-full)", transition: "width var(--transition)" }} />
              </div>
              <span style={{ fontSize: "0.875rem", fontWeight: 700, color: threatColor }}>{threatLevel}%</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Response Budget</div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <div style={{ flex: 1, height: "8px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                <div style={{ width: `${remainingBudget}%`, height: "100%", background: "var(--primary-500)", borderRadius: "var(--radius-full)", transition: "width var(--transition)" }} />
              </div>
              <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "var(--primary-600)" }}>{remainingBudget}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
        <div className="card" style={{ padding: "var(--space-3)", maxHeight: "300px", overflowY: "auto" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.5rem" }}>Live Event Feed</div>
          {visibleEvents.length === 0 && <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Waiting for events...</p>}
          {visibleEvents.map((evt) => {
            const colorMap = { alert: "var(--warning-500)", info: "var(--primary-500)", warning: "var(--warning-500)", critical: "var(--error-500)" };
            const bgMap = { alert: "var(--warning-50)", info: "var(--primary-50)", warning: "var(--warning-50)", critical: "var(--error-50)" };
            return (
              <div key={evt.id} style={{ padding: "0.5rem", borderRadius: "var(--radius-sm)", marginBottom: "0.375rem", background: bgMap[evt.type], border: `1px solid ${colorMap[evt.type]}30` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.125rem" }}>
                  <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: colorMap[evt.type], textTransform: "uppercase" }}>{evt.domain}</span>
                  <span style={{ fontSize: "0.625rem", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{evt.timestamp}</span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text)" }}>{evt.message}</div>
              </div>
            );
          })}
        </div>

        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.5rem" }}>Available Actions</div>
          {actions.map((action) => {
            const taken = takenActions.includes(action.id);
            const affordable = remainingBudget >= action.cost;
            return (
              <button
                key={action.id}
                onClick={() => handleAction(action)}
                disabled={taken || !affordable || !running}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "var(--radius-sm)",
                  border: taken ? "1px solid var(--accent-300)" : "1px solid var(--border)",
                  background: taken ? "var(--accent-50)" : affordable ? "var(--surface)" : "var(--neutral-100)",
                  cursor: taken || !affordable || !running ? "not-allowed" : "pointer",
                  textAlign: "left",
                  marginBottom: "0.375rem",
                  opacity: taken || !affordable ? 0.6 : 1,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.125rem" }}>
                  <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{action.label}</span>
                  <span style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>
                    {taken ? "\u2713 Done" : `Cost: ${action.cost}`}
                  </span>
                </div>
                <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>{action.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {finished && (
        <div className="card" style={{ padding: "var(--space-4)", marginTop: "var(--space-3)", textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>{threatLevel <= 0 ? "\u2705" : "\u26A0"}</div>
          <h3 style={{ fontSize: "1.125rem", marginBottom: "0.25rem" }}>{threatLevel <= 0 ? "Threat Neutralized!" : "Simulation Ended"}</h3>
          <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
            Final threat level: {threatLevel}% — Budget remaining: {remainingBudget}
          </p>
          <button onClick={onClose} className="btn btn-primary">Continue</button>
        </div>
      )}
    </div>
  );
}
